import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import { Trophy } from "lucide-react";
import Header from "../components/Header";
import Breadcrumb from "../components/Breadcrumb";
import LeagueContextActions from "../components/LeagueContextActions";
import ProviderBetaBanner from "../components/ProviderBetaBanner";
import LeagueCupSettings from "../components/LeagueCupSettings";
import ChampionTile from "../components/leagueCup/ChampionTile";
import CupFactTile from "../components/leagueCup/CupFactTile";
import CupBracketPanel from "../components/leagueCup/CupBracketPanel";
import CupGroupCard from "../components/leagueCup/CupGroupCard";
import WildcardRacePanel from "../components/leagueCup/WildcardRacePanel";
import { computeCupFacts, formatWeekRange } from "../components/leagueCup/cupStats";
import { safeFetch } from "../utils/api";
import usePageMeta from "../utils/usePageMeta";
import { trackClarityEvent } from "../utils/clarity";

const STATUS_ORDER = { draft: 0, group_stage: 1, knockout: 2, completed: 3 };

/** Hero chips marking each phase of the Cup as upcoming, active, or done. */
const PhasePills = ({ cup }) => {
    const order = STATUS_ORDER[cup.status] ?? 0;
    const groupWeeks = cup.schedule?.group_stage_weeks || [];
    const rounds = cup.schedule?.knockout_rounds || [];
    const bracketByIndex = Object.fromEntries(
        (cup.bracket || []).map((round) => [round.round_index, round])
    );

    const pills = [
        {
            key: "groups",
            label: `Group Stage · ${formatWeekRange(
                groupWeeks[0],
                groupWeeks[groupWeeks.length - 1]
            )}`,
            done: order >= 2,
            active: cup.status === "group_stage",
        },
        ...rounds.map((round) => ({
            key: `round-${round.round_index}`,
            label: `${round.label} · ${formatWeekRange(round.start_week, round.end_week)}`,
            done:
                cup.status === "completed" ||
                bracketByIndex[round.round_index]?.is_complete === true,
            active:
                cup.status === "knockout" &&
                bracketByIndex[round.round_index]?.is_complete !== true,
        })),
    ];
    // Only the first not-done knockout round is "active".
    let seenActive = false;
    pills.forEach((pill) => {
        if (pill.active && seenActive) pill.active = false;
        if (pill.active) seenActive = true;
    });

    return (
        <div className="flex max-w-full flex-wrap gap-2">
            {pills.map((pill) => (
                <span
                    key={pill.key}
                    className={`rounded-full border px-3.5 py-[7px] text-[10.5px] font-sans font-extrabold uppercase tracking-wide ${
                        pill.done
                            ? "border-green-mid bg-green-mid/20 text-green-mid"
                            : pill.active
                            ? "border-mustard bg-mustard/[0.18] text-mustard"
                            : "border-white/25 text-white/65"
                    }`}
                >
                    {pill.done ? "✓ " : ""}
                    {pill.label}
                </span>
            ))}
            {cup.status === "completed" && (
                <span className="rounded-full border border-mustard bg-mustard/[0.18] px-3.5 py-[7px] text-[10.5px] font-sans font-extrabold uppercase tracking-wide text-mustard">
                    🏆 Completed
                </span>
            )}
        </div>
    );
};

const LoadingSkeleton = () => (
    <div className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="h-44 animate-pulse rounded-2xl bg-surface border border-border xl:row-span-2" />
            {Array.from({ length: 6 }, (_, i) => (
                <div
                    key={i}
                    className="h-[84px] animate-pulse rounded-2xl bg-surface border border-border"
                />
            ))}
        </div>
        <div className="h-56 animate-pulse rounded-2xl bg-surface border border-border" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }, (_, i) => (
                <div
                    key={i}
                    className="h-56 animate-pulse rounded-2xl bg-surface border border-border"
                />
            ))}
        </div>
    </div>
);

const EmptyState = ({ canEdit, onSetup }) => (
    <div className="rounded-2xl border border-border-card bg-surface p-8 text-center shadow-card">
        <Trophy className="mx-auto mb-3 h-10 w-10 text-mustard" />
        <h3 className="mb-1 font-display text-xl font-bold text-ds-text">
            No League Cup yet
        </h3>
        <p className="mb-4 text-sm text-text-muted">
            The commissioner hasn't set up a League Cup for this season.
        </p>
        {canEdit && (
            <button
                type="button"
                onClick={onSetup}
                className="inline-block rounded-lg bg-mustard px-4 py-2 font-sans text-sm font-bold text-forest transition-colors hover:bg-yellow-400"
            >
                Set up League Cup
            </button>
        )}
    </div>
);

const LeagueCupPage = () => {
    const { provider, leagueYear, leagueId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [leagueData, setLeagueData] = useState(null);
    const [cupData, setCupData] = useState(null);
    const [cupLoading, setCupLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [activeTab, setActiveTab] = useState(() =>
        new URLSearchParams(location.search).get("tab") === "settings"
            ? "settings"
            : "overview"
    );
    // Bumped by the settings tab after any mutation so the overview refetches.
    const [refreshKey, setRefreshKey] = useState(0);

    const handleTabChange = (nextTab) => {
        setActiveTab(nextTab);
        const queryParams = new URLSearchParams(location.search);
        if (nextTab === "settings") queryParams.set("tab", "settings");
        else queryParams.delete("tab");
        const query = queryParams.toString();
        window.history.replaceState(
            null,
            "",
            `${location.pathname}${query ? `?${query}` : ""}`
        );
    };

    const cupName = cupData?.config?.name || "League Cup";
    const facts = useMemo(
        () => (cupData?.is_configured ? computeCupFacts(cupData) : []),
        [cupData]
    );

    usePageMeta({
        title: leagueData
            ? `${leagueData.league_name} ${cupName} (${leagueYear})`
            : `League Cup — ${leagueYear}`,
        description: leagueData
            ? `${cupName} for ${leagueData.league_name} (${leagueYear}) — group stage standings and knockout bracket.`
            : undefined,
        canonical: `/fantasy_stats/league-cup/${provider}/${leagueYear}/${leagueId}/`,
    });

    if (fetchError) {
        throw fetchError;
    }

    // Fetch league metadata for hero band
    useEffect(() => {
        safeFetch(`/api/league/${provider}/${leagueYear}/${leagueId}/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    setLeagueData(data);
                }
            })
            .catch((err) => {
                console.error("ERROR: /api/league/:provider/:year/:id", leagueYear, leagueId, err);
                setFetchError(err);
            });
    }, [provider, leagueYear, leagueId, navigate]);

    useEffect(() => {
        trackClarityEvent("league_cup_page_viewed");
    }, [provider, leagueYear, leagueId]);

    useEffect(() => {
        let cancelled = false;
        setCupLoading(true);
        // Note: the previous payload is intentionally kept while the refetch
        // is in flight. Settings saves bump refreshKey, and the overview
        // refreshes in the background instead of flashing a loading skeleton
        // (which also unmounted the settings tab mid-save).
        // Append refreshKey as a cache-buster so any intermediate HTTP cache
        // (browser heuristic, CDN, etc.) returns a fresh payload after a
        // settings save instead of replaying a stale response.
        const cupUrl =
            `/api/league/${provider}/${leagueYear}/${leagueId}/cup/` +
            (refreshKey > 0 ? `?_=${refreshKey}` : "");
        safeFetch(cupUrl, {}, false, 2)
            .then((data) => {
                if (!cancelled) setCupData(data);
            })
            .catch((err) => {
                console.error("Error fetching league cup:", err);
                if (!cancelled) setFetchError(err);
            })
            .finally(() => {
                if (!cancelled) setCupLoading(false);
            });

        return () => { cancelled = true; };
    }, [provider, leagueYear, leagueId, refreshKey]);

    const leagueUrl = `/fantasy_stats/league/${provider}/${leagueYear}/${leagueId}/`;
    const canEdit = cupData?.permissions?.can_edit === true;
    const isConfigured = cupData?.is_configured === true;

    const teamCount = (cupData?.groups || []).reduce(
        (count, group) => count + (group.table?.length || 0),
        0
    );
    const structureLine = isConfigured
        ? [
              teamCount ? `${teamCount} teams` : null,
              `${cupData.config.group_count} groups`,
              `${cupData.schedule?.bracket_size}-team knockout`,
          ]
              .filter(Boolean)
              .join(" · ")
        : null;
    const groupGridClass =
        (cupData?.groups || []).length <= 2
            ? "grid gap-4 lg:grid-cols-2"
            : "grid gap-4 md:grid-cols-2 xl:grid-cols-3";

    return (
        <div className="min-h-screen bg-bg">
            <Header />

            {/* Hero Band */}
            <div className="bg-forest border-b-4 border-mustard">
                <div className="max-w-6xl mx-auto px-6 py-6">
                    <Breadcrumb
                        items={[
                            { label: "Dashboard", to: "/dashboard" },
                            {
                                label: leagueData?.league_name ?? "…",
                                to: leagueData ? leagueUrl : undefined,
                            },
                            { label: "League Cup" },
                        ]}
                    />
                    <div className="flex min-w-0 flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0">
                            <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                {leagueYear} {cupName}
                            </p>
                            {leagueData ? (
                                <>
                                    <h1 className="font-display text-4xl font-black text-white leading-none break-words">
                                        {leagueData.league_name}
                                    </h1>
                                    <p className="text-white/50 font-sans text-sm font-semibold mt-2 break-words">
                                        {structureLine || `League ID: ${leagueData.league_id}`}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="h-9 w-64 bg-white/20 rounded animate-pulse mb-2" />
                                    <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
                                </>
                            )}
                        </div>
                        <div className="flex min-w-0 max-w-full flex-col items-start gap-3 lg:items-end">
                            <LeagueContextActions
                                league={leagueData}
                                provider={provider}
                                leagueYear={leagueYear}
                                leagueId={leagueId}
                                activePage="cup"
                            />
                            {isConfigured && <PhasePills cup={cupData} />}
                        </div>
                    </div>
                </div>
            </div>

            {/* Cup Content */}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
                <ProviderBetaBanner
                    provider={provider}
                    leagueId={leagueId}
                    leagueYear={leagueYear}
                    leagueName={leagueData?.league_name}
                />

                <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
                    {canEdit && (
                        <div className="mb-6 max-w-full overflow-x-auto">
                            <Tabs.List className="inline-flex min-w-max gap-1 rounded-lg border border-border bg-surface p-1 shadow-sm">
                                {[
                                    { value: "overview", label: "Overview" },
                                    { value: "settings", label: "Cup Settings" },
                                ].map(({ value, label }) => (
                                    <Tabs.Trigger
                                        key={value}
                                        value={value}
                                        className="
                                            px-3 py-2 text-xs font-sans font-bold uppercase tracking-wide
                                            rounded whitespace-nowrap transition-colors duration-150
                                            text-text-muted hover:text-forest
                                            data-[state=active]:bg-forest data-[state=active]:text-white
                                        "
                                    >
                                        {label}
                                    </Tabs.Trigger>
                                ))}
                            </Tabs.List>
                        </div>
                    )}

                    <Tabs.Content value="overview" className="space-y-6 outline-none">
                        {renderOverview()}
                    </Tabs.Content>

                    {canEdit && (
                        <Tabs.Content value="settings" className="outline-none">
                            {leagueData?.id ? (
                                <LeagueCupSettings
                                    leaguePk={leagueData.id}
                                    onChanged={() => setRefreshKey((k) => k + 1)}
                                />
                            ) : (
                                <div className="h-40 animate-pulse rounded-2xl border border-border bg-surface" />
                            )}
                        </Tabs.Content>
                    )}
                </Tabs.Root>
            </main>
        </div>
    );

    function renderOverview() {
        if (cupLoading && !cupData) {
            return <LoadingSkeleton />;
        }
        if (!isConfigured) {
            return (
                <EmptyState
                    canEdit={canEdit}
                    onSetup={() => handleTabChange("settings")}
                />
            );
        }
        return (
            <>
                        {/* Champion spotlight + cup facts */}
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <div
                                className={`md:col-span-2 xl:col-span-1 ${
                                    facts.length > 3 ? "xl:row-span-2" : ""
                                }`}
                            >
                                <ChampionTile cup={cupData} />
                            </div>
                            {facts.map((fact) => (
                                <CupFactTile key={fact.key} fact={fact} />
                            ))}
                        </div>

                        {/* Group stage */}
                        <div>
                            <div className="mb-4 flex items-baseline justify-between px-0.5">
                                <h3 className="font-display text-[22px] font-black text-ds-text">
                                    Group Stage
                                </h3>
                                <span className="text-[11px] font-sans font-bold uppercase tracking-wider text-text-muted">
                                    {formatWeekRange(
                                        cupData.schedule?.group_stage_weeks?.[0],
                                        cupData.schedule?.group_stage_weeks?.slice(-1)[0]
                                    )}{" "}
                                    · total points for
                                </span>
                            </div>
                            <div className={groupGridClass}>
                                {(cupData.groups || []).map((group) => (
                                    <CupGroupCard
                                        key={group.name}
                                        group={group}
                                        groupWeeks={cupData.schedule?.group_stage_weeks}
                                        weekLabels={cupData.week_labels}
                                    />
                                ))}
                            </div>
                            <div className="mt-4 flex flex-wrap justify-center gap-3 text-[11px] font-sans font-bold text-text-muted sm:gap-5">
                                <span>
                                    <span className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-green-mid align-[-1px]" />
                                    Group winner — qualifies
                                </span>
                                {Number(cupData.schedule?.wildcard_count) > 0 && (
                                    <span>
                                        <span className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-mustard align-[-1px]" />
                                        Wildcard — best non-winner
                                    </span>
                                )}
                            </div>
                        </div>

                {/* Wildcard race */}
                <WildcardRacePanel cup={cupData} />

                {/* Knockout bracket */}
                <CupBracketPanel cup={cupData} />
            </>
        );
    }
};

export default LeagueCupPage;
